import { useCallback, useMemo } from 'react';
import { Countdown } from 'src/components/common/CountDown/CountDown';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { useOnSaleEventsContext } from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { vars } from 'src/core/themes';
import { Button, Stack, TooltipPopover } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { LockedNewIcon } from 'src/svgs';
import { IconsFill, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import { EventDiscoveryQuotaConfig, SectionInfo } from 'src/WebApiController';

import { OnSaleEventDetailsHeader } from '../OnSaleEventDetailsHeader';
import * as styles from './OnSaleEventMetrics.css';
import { OnSaleEventMetricsLocked } from './OnSaleEventMetricsLocked';
import { OnSaleEventMetricsTiles } from './OnSaleEventMetricsTiles';
import { UnlockMetricsDialog } from './UnlockMetricsDialog';

export const OnSaleEventMetrics = ({
  selectedSections,
}: {
  selectedSections?: SectionInfo[];
}) => {
  const { onUnlockDemandData, onSaleEventAccess } = useOnSaleEventsContext();
  const unlockMetricsDialog = useBasicDialog();

  const onUnlock = useCallback(
    (quotaConfig: EventDiscoveryQuotaConfig) => {
      onUnlockDemandData(quotaConfig);
      unlockMetricsDialog.closeDialog();
    },
    [onUnlockDemandData, unlockMetricsDialog]
  );

  const onOpenDialog = useCallback(
    () => unlockMetricsDialog.launchDialog(),
    [unlockMetricsDialog]
  );

  return (
    <>
      <OnSaleEventDetailsHeader
        titleContentId={ContentId.EventMetrics}
        titlePostContent={
          !onSaleEventAccess?.recentViewed ? (
            <LockedNewIcon
              stroke={IconsFill.currentColor}
              size={vars.iconSize.m}
            />
          ) : undefined
        }
        rightContent={
          <OnSaleEventDetailsHeaderAction onOpenDialog={onOpenDialog} />
        }
      />
      {!onSaleEventAccess?.recentViewed ? (
        <OnSaleEventMetricsLocked onOpenDialog={onOpenDialog} />
      ) : (
        <OnSaleEventMetricsTiles selectedSections={selectedSections} />
      )}

      <UnlockMetricsDialog
        {...unlockMetricsDialog.dialogProps}
        onCancel={unlockMetricsDialog.closeDialog}
        onOkay={onUnlock}
      />
    </>
  );
};

const OnSaleEventDetailsHeaderAction = ({
  onOpenDialog,
}: {
  onOpenDialog: () => void;
}) => {
  const { onSaleEventAccess } = useOnSaleEventsContext();

  const canUpdatePlan = useMemo(() => {
    if (
      onSaleEventAccess?.eventDiscoveryConfig == null ||
      onSaleEventAccess?.eventDiscoveryQuota == null
    ) {
      // No quota yet
      return true;
    }

    return onSaleEventAccess.eventDiscoveryConfig.quotaConfigs
      .filter(
        ({ eventViewQuota }) =>
          eventViewQuota !==
          onSaleEventAccess.eventDiscoveryQuota!.eventViewQuota
      )
      .some(
        ({ eventViewQuota }) =>
          eventViewQuota >
          onSaleEventAccess.eventDiscoveryQuota!.eventViewQuotaUsed
      );
  }, [
    onSaleEventAccess?.eventDiscoveryConfig,
    onSaleEventAccess?.eventDiscoveryQuota,
  ]);

  if (
    onSaleEventAccess?.noProceedsAdvantage ||
    onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuota == null ||
    onSaleEventAccess?.eventDiscoveryQuota?.eventViewQuotaUsed == null
  ) {
    return null;
  }

  return (
    <Stack direction="row" gap="s" alignItems="center">
      <FormatContent
        id={FormatContentId.EventViewsRemaining}
        params={[
          Math.max(
            0,
            onSaleEventAccess.eventDiscoveryQuota.eventViewQuota -
              onSaleEventAccess.eventDiscoveryQuota.eventViewQuotaUsed
          ) + '',
        ]}
      />

      {onSaleEventAccess.eventDiscoveryQuota
        .eventViewQuotaUsedNextResetDate && (
        <TooltipPopover
          variant="textPlain"
          textColor="primary"
          triggerContent={
            <InfoOutlineIcon withHoverEffect size={vars.iconSize.s} />
          }
        >
          <Stack direction="column">
            <Content id={ContentId.EventViewsWillBeResetIn} />
            <Countdown
              targetDate={stringToUtcDate(
                onSaleEventAccess.eventDiscoveryQuota
                  .eventViewQuotaUsedNextResetDate
              )}
            />
          </Stack>
        </TooltipPopover>
      )}

      {canUpdatePlan && (
        <Button
          className={styles.UpdatePlanButton}
          variant="textPlain"
          onClick={onOpenDialog}
        >
          <Content id={ContentId.UpgradeAccess} />
        </Button>
      )}
    </Stack>
  );
};
